/**
 * Performance tags of the active stats tab group (list, text, gateway, path, lander,
 * offer, drop, source), moved out of StatsSection unchanged. Fetches are deferred so
 * drop stats load first: tags run in the background after a short delay to avoid
 * competing for the network/connection pool. A group whose tracker this deployment
 * has not enabled (Tres Commas without keys) fetches none; Voluum/Binom always do.
 * Returns the list tags query (the Drops, List and Sources tabs read it).
 * For Media Devoted | Created By Ciro Autiero
 */

import { useEffect, useMemo, useState } from 'react';
import { trackerFromEnum } from '@trafficpulse/shared';

import { buildListTagQueryParams } from '@/features/smswiser-campaigns/utils/listTagQueryParams';
import { useEnabledTrackers } from '@/features/trackers/useEnabledTrackers';
import {
  useListPerformanceTags,
  useTextPerformanceTags,
  useGatewayPerformanceTags,
  usePathPerformanceTags,
  useLanderPerformanceTags,
  useOfferPerformanceTags,
  useDropPerformanceTags,
  useSourcePerformanceTags,
} from '@/hooks';
import type { TabGroup } from '@/pages/admin/SmswiserCampaignDetailPage/types';

export function useStatsPerfTags(countryKey: string, group: TabGroup | undefined) {
  const groupWiserIds = group?.wiserIds ?? [];
  const listTagParams = useMemo(() => buildListTagQueryParams(), []);
  const tagParams = groupWiserIds.length > 0 ? listTagParams : undefined;
  const wiserIds = groupWiserIds.length > 0 ? groupWiserIds : undefined;
  const readable = useEnabledTrackers().includes(trackerFromEnum(group?.trafficProvider ?? 'VOLUUM'));

  const [deferred, setDeferred] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDeferred(true), 500);
    return () => clearTimeout(t);
  }, []);
  const tagsFetchEnabled = deferred && readable;

  const perfTagsQuery = useListPerformanceTags(countryKey, wiserIds, tagParams, tagsFetchEnabled);
  useTextPerformanceTags(countryKey, wiserIds, tagParams, tagsFetchEnabled);
  useGatewayPerformanceTags(countryKey, wiserIds, tagParams, tagsFetchEnabled);
  usePathPerformanceTags(countryKey, wiserIds, tagParams, tagsFetchEnabled);
  useLanderPerformanceTags(countryKey, wiserIds, tagParams, tagsFetchEnabled);
  useOfferPerformanceTags(countryKey, wiserIds, tagParams, tagsFetchEnabled);
  useDropPerformanceTags(countryKey, wiserIds, tagParams, tagsFetchEnabled);
  useSourcePerformanceTags(countryKey, wiserIds, tagParams, tagsFetchEnabled);
  return perfTagsQuery;
}
